import { useEffect } from "react";
import { defaultTimeZone, type DefaultTimeZone } from "@visioni/a3s-monitoring/app";

export interface ListTimeZone {
  /** The zone the list reads its range in and shows its times in; `undefined` until the default is known. */
  readonly timeZone: string | undefined;
  /** What "Default" means for the current site, or `undefined` while its zone is loading. */
  readonly fallback: DefaultTimeZone | undefined;
  /** What "Default" would mean for another site, for carrying the zone across a site change. */
  readonly defaultFor: (site: string | undefined) => DefaultTimeZone | undefined;
}

const viewerTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * The zone a Patriot tools list is in, from the URL's `tz` or else the default.
 *
 * A URL without one — an older link, or arriving from the menu — has the
 * default written into it, so the link copied from the page always names its
 * zone. That waits for the selected site's zone to load: writing the viewer's
 * zone first would capture it and then flip.
 */
export function useListTimeZone({
  tz,
  site,
  siteTimeZone,
  onResolve,
}: {
  readonly tz: string | undefined;
  readonly site: string | undefined;
  readonly siteTimeZone: (siteId: string) => string | null | undefined;
  readonly onResolve: (timeZone: string) => void;
}): ListTimeZone {
  const defaultFor = (forSite: string | undefined): DefaultTimeZone | undefined => {
    if (forSite === undefined) return defaultTimeZone({ viewerTimeZone: viewerTimeZone() });
    const zone = siteTimeZone(forSite);
    return zone === undefined ? undefined : defaultTimeZone({ siteTimeZone: zone, viewerTimeZone: viewerTimeZone() });
  };
  const fallback = defaultFor(site);
  const resolved = fallback?.timeZone;

  useEffect(() => {
    if (tz === undefined && resolved !== undefined) onResolve(resolved);
  }, [tz, resolved, onResolve]);

  return { timeZone: tz ?? resolved, fallback, defaultFor };
}
